
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import ScannerView from './components/ScannerView';
import SettingsView from './components/SettingsView';
import AppointmentListView from './components/AppointmentListView';
import AppointmentDetailView from './components/AppointmentDetailView';
import { VerificationResponse, Appointment, AppointmentStatus, SettingsData } from './types';
import { verifyVoucher } from './services/geminiService';

const Toast = ({ message, visible, onHide }: { message: string, visible: boolean, onHide: () => void }) => {
  useEffect(() => {
    let timer: number;
    if (visible) {
      timer = window.setTimeout(() => {
        onHide();
      }, 3000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [visible, onHide]); 

  if (!visible) return null;

  return (
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[2000] animate-in fade-in zoom-in duration-300 pointer-events-none">
      <div className="bg-black/80 backdrop-blur-md text-white px-6 py-3 rounded-2xl text-sm font-bold shadow-2xl">
        {message}
      </div>
    </div>
  );
};

const MOCK_APPOINTMENTS: Appointment[] = [
  { 
    id: '11', orderId: '20240126001', name: '张先生', peopleCount: 3, phone: '130****2222', status: '待核销', timeSlot: '上午场 10:00-11:30', dateLabel: '今天',
    productName: '工作日冰室自助', merchant: '满记甜品之心城店', appointmentDate: '2026-01-16', orderSource: '微信支付', note: '备注内容'
  },
  { 
    id: '12', orderId: '20240126002', name: '李女士', peopleCount: 2, phone: '138****5555', status: '待核销', timeSlot: '上午场 10:00-11:30', dateLabel: '今天',
    productName: '工作日冰室自助', merchant: '满记甜品之心城店', appointmentDate: '2026-01-16', orderSource: '抖音券', note: ''
  },
  { 
    id: '13', orderId: '20240126003', name: '王先生', peopleCount: 4, phone: '139****1111', status: '已完成', timeSlot: '上午场 10:00-11:30', dateLabel: '今天',
    productName: '工作日冰室自助', merchant: '满记甜品之心城店', appointmentDate: '2026-01-16', orderSource: '微信支付', note: ''
  },
  { 
    id: '14', orderId: '20240126004', name: '赵先生', peopleCount: 1, phone: '135****8888', status: '已退款', timeSlot: '中午场 11:30-13:00', dateLabel: '今天',
    productName: '工作日冰室自助', merchant: '满记甜品之心城店', appointmentDate: '2026-01-16', orderSource: '美团', note: ''
  },
  { 
    id: '15', orderId: '20240126005', name: '陈小姐', peopleCount: 2, phone: '137****9999', status: '已取消', timeSlot: '中午场 11:30-13:00', dateLabel: '今天',
    productName: '工作日冰室自助', merchant: '满记甜品之心城店', appointmentDate: '2026-01-16', orderSource: '微信支付', note: ''
  },
  { 
    id: '16', orderId: '20240126006', name: '孙先生', peopleCount: 2, phone: '188****8888', status: '已失效', timeSlot: '下午场 13:00-15:00', dateLabel: '今天',
    productName: '工作日冰室自助', merchant: '满记甜品之心城店', appointmentDate: '2026-01-16', orderSource: '微信支付', note: ''
  },
  { 
    id: '17', orderId: '20240127001', name: '周女士', peopleCount: 2, phone: '131****1111', status: '待核销', timeSlot: '上午场 10:00-11:30', dateLabel: '明天',
    productName: '周末冰室自助', merchant: '满记甜品之心城店', appointmentDate: '2026-01-17', orderSource: '微信支付', note: ''
  },
  { 
    id: '18', orderId: '20240127002', name: '吴先生', peopleCount: 5, phone: '132****2222', status: '待核销', timeSlot: '下午场 13:00-15:00', dateLabel: '明天',
    productName: '周末冰室自助', merchant: '满记甜品之心城店', appointmentDate: '2026-01-17', orderSource: '微信支付', note: ''
  }
];

const DEFAULT_SETTINGS: SettingsData = {
  mode: 'brand' as 'brand' | 'custom',
  dateGroups: [
    {
      id: 'dg1',
      days: [1, 2, 3, 4, 5],
      slots: [
        { 
          id: 'w1', name: '上午场', time: '10:00-11:30', maxCapacity: 20, 
          product: { id: 'p1', name: '工作日冰室自助', price: '68.00', image: 'https://images.unsplash.com/photo-1544145945-f904253db0ad?w=100&h=100&fit=crop' } 
        }
      ]
    },
    {
      id: 'dg2',
      days: [6, 7],
      slots: [
        { 
          id: 'e1', name: '全天场', time: '10:00-22:00', maxCapacity: 50, 
          product: { id: 'p2', name: '周末冰室自助', price: '78.00', image: 'https://images.unsplash.com/photo-1551024601-bec78aea704b?w=100&h=100&fit=crop' } 
        }
      ]
    }
  ]
};

const App: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'redeem' | 'appointments'>('redeem');
  const [manualCode, setManualCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [toast, setToast] = useState<{ visible: boolean, message: string }>({ visible: false, message: '' });
  
  const [flowState, setFlowState] = useState<'details' | 'error' | null>(null);
  const [errorType, setErrorType] = useState<'shop_mismatch' | 'invalid_code' | null>(null);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const hideToast = useCallback(() => {
    setToast(prev => ({ ...prev, visible: false }));
  }, []);

  const showToast = useCallback((message: string) => {
    setToast({ visible: true, message });
  }, []);

  const handleVerification = useCallback(async (code: string) => {
    if (!code.trim()) return;
    setIsVerifying(true);
    setIsScannerOpen(false);
    const response = await verifyVoucher(code);
    if (response.isValid) {
      setSelectedAppointment(MOCK_APPOINTMENTS[0]);
      setFlowState('details');
    } else {
      setErrorType(response.message === "SHOP_MISMATCH" ? 'shop_mismatch' : 'invalid_code');
      setFlowState('error');
    }
    setIsVerifying(false);
    setManualCode('');
  }, []);

  const resetFlow = useCallback(() => {
    setFlowState(null);
    setSelectedAppointment(null);
    setErrorType(null);
  }, []);

  const openAppointment = (app: Appointment) => {
    setSelectedAppointment(app);
    setFlowState('details');
  };

  const reqKey = useMemo(() => {
    if (isSettingsOpen) return 'settings';
    if (flowState === 'details') return 'detail';
    return activeTab;
  }, [isSettingsOpen, flowState, activeTab]);

  return (
    <div className="h-screen w-full bg-[#F2F4F7] flex overflow-hidden font-sans">
      <div className="w-[460px] h-full overflow-y-auto flex items-center justify-center shrink-0 z-[200] py-8 no-scrollbar">
        <div className="w-[375px] h-[800px] shrink-0 bg-[#1C1C1E] border-[8px] border-[#1C1C1E] rounded-[3.5rem] shadow-[0_40px_100px_-20px_rgba(0,0,0,0.3)] overflow-hidden relative">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-5 bg-[#1C1C1E] rounded-b-2xl z-[500]"></div>
          
          <div className="w-full h-full bg-white flex flex-col relative overflow-hidden">
            <div className="bg-white px-7 pt-4 pb-2 flex justify-between items-center z-[400] shrink-0">
              <div className="text-[13px] font-bold text-gray-900">
                {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
              </div>
              <div className="flex items-center space-x-2">
                <svg className="w-3.5 h-3.5 text-gray-900" fill="currentColor" viewBox="0 0 24 24"><path d="M12 21l-12-18h24z" /></svg>
                <div className="relative w-5 h-2.5 border border-gray-400 rounded-[2px] p-0.5 flex items-center">
                  <div className="bg-gray-900 h-full w-4/5 rounded-px"></div>
                </div>
              </div>
            </div>

            <main className="flex-1 relative flex flex-col overflow-hidden">
              <div className="flex-1 flex flex-col bg-[#F9FAFB] min-h-0">
                 {activeTab === 'redeem' ? (
                   <div className="flex-1 flex flex-col min-h-0">
                     <header className="bg-white px-4 h-12 flex items-center justify-between shrink-0 border-b border-gray-100">
                       <div className="w-10 shrink-0"></div>
                       <h1 className="text-base font-bold text-[#333] truncate">核销管理</h1>
                       <button 
                         onClick={() => setIsSettingsOpen(true)} 
                         className="w-10 h-10 shrink-0 flex items-center justify-center text-gray-400 active:bg-gray-50 rounded-full transition-all"
                       >
                         <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                         </svg>
                       </button>
                     </header>
                     <div className="flex-1 p-5 space-y-12 overflow-y-auto no-scrollbar pb-24">
                       <div className="flex flex-col items-center justify-center py-6">
                         <button onClick={() => setIsScannerOpen(true)} className="w-44 h-44 bg-white rounded-3xl shadow-[0_20px_40px_-10px_rgba(0,0,0,0.05)] flex flex-col items-center justify-center active:scale-95 transition-all border border-gray-100/50">
                            <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4 ring-8 ring-green-50/50">
                              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01" /></svg>
                            </div>
                            <span className="text-gray-900 font-bold text-lg">扫码核销</span>
                         </button>
                       </div>
                       <div className="space-y-4">
                         <input 
                           type="text" 
                           value={manualCode} 
                           onChange={(e) => setManualCode(e.target.value)} 
                           placeholder="请输入券码" 
                           className="w-full h-12 bg-white border border-gray-200 rounded-xl px-4 text-base shadow-sm focus:border-green-400 outline-none transition-all placeholder:text-gray-300" 
                         />
                         <button 
                           onClick={() => handleVerification(manualCode)}
                           disabled={!manualCode.trim() || isVerifying}
                           className={`w-full h-12 rounded-xl font-bold text-white transition-all shadow-md active:scale-[0.98] ${!manualCode.trim() || isVerifying ? 'bg-gray-200 text-gray-400' : 'bg-[#1C1C1E]'}`}
                         >
                           {isVerifying ? '正在核销...' : '确认核销'}
                         </button>
                       </div>
                     </div>
                   </div>
                 ) : (
                   <AppointmentListView 
                     appointments={MOCK_APPOINTMENTS} 
                     onSelect={openAppointment} 
                     onOpenSettings={() => setIsSettingsOpen(true)}
                   />
                 )}

                 <nav className="bg-white border-t border-gray-50 flex items-stretch h-20 px-4 pb-6 absolute bottom-0 left-0 right-0 z-[400]">
                   <button onClick={() => setActiveTab('redeem')} className={`flex-1 flex flex-col items-center justify-center space-y-1 transition-colors ${activeTab === 'redeem' ? 'text-[#5DA756] font-bold' : 'text-gray-300'}`}>
                     <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01" /></svg>
                     <span className="text-[11px]">核销</span>
                   </button>
                   <button onClick={() => setActiveTab('appointments')} className={`flex-1 flex flex-col items-center justify-center space-y-1 transition-colors ${activeTab === 'appointments' ? 'text-[#5DA756] font-bold' : 'text-gray-300'}`}>
                     <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                     <span className="text-[11px]">预约单</span>
                   </button>
                 </nav>
              </div>

              {isScannerOpen && <div className="absolute inset-0 z-[500]"><ScannerView onScan={handleVerification} onClose={() => setIsScannerOpen(false)} /></div>}
              {isSettingsOpen && <div className="absolute inset-0 z-[600]"><SettingsView initialSettings={settings} onClose={() => setIsSettingsOpen(false)} onSave={(s) => {setSettings(s); setIsSettingsOpen(false);}} /></div>}
              
              <Toast message={toast.message} visible={toast.visible} onHide={hideToast} />

              {flowState === 'details' && selectedAppointment && (
                <div className="absolute inset-0 z-[700]">
                  <AppointmentDetailView 
                    appointment={selectedAppointment} 
                    onClose={resetFlow} 
                    onConfirmRedeem={() => { 
                      showToast('核销成功'); 
                      resetFlow(); 
                    }} 
                    onConfirmRefund={() => { 
                      showToast('退款成功'); 
                      resetFlow(); 
                    }} 
                  />
                </div>
              )}
              
              {flowState === 'error' && (
                <div className="absolute inset-0 z-[800] flex items-center justify-center px-8 animate-in fade-in duration-200">
                  <div className="absolute inset-0 bg-black/60 backdrop-blur-[4px]" onClick={resetFlow}></div>
                  <div className="relative bg-white w-full rounded-[2.5rem] shadow-2xl p-8 flex flex-col items-center animate-in zoom-in-95 duration-200">
                    <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-6">
                      <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                    </div>
                    <h3 className="text-lg font-black text-gray-900 mb-2">核销失败</h3>
                    <p className="text-gray-500 text-sm text-center mb-8 font-medium">
                      {errorType === 'shop_mismatch' ? '该券码绑定的门店与当前门店不匹配，请引导用户至对应门店使用。' : '无效券码或券码已过期，请核对后重试。'}
                    </p>
                    <button onClick={resetFlow} className="w-full h-12 bg-gray-900 text-white rounded-2xl font-black active:scale-[0.98] transition-all">返回重试</button>
                  </div>
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
      
      <div className="flex-1 h-full bg-white border-l border-gray-100 shadow-[0_0_100px_rgba(0,0,0,0.02)] overflow-y-auto p-10 lg:p-16 no-scrollbar">
        <div className="max-w-2xl mx-auto space-y-12 animate-in fade-in duration-500" key={reqKey}>
          <header className="space-y-6">
            <h2 className="text-[18px] font-black text-[#1C1C1E] tracking-tight leading-tight">
              {reqKey === 'settings' ? '【预约设置】需求说明' : 
               reqKey === 'detail' ? '【预约单详情】需求说明' : 
               reqKey === 'redeem' ? '【核销管理】需求说明' : '【预约单管理】需求说明'}
            </h2>
            <div className="h-1 w-20 bg-[#5DA756] rounded-full"></div>
          </header>

          <section className="space-y-10">
            {reqKey === 'settings' ? (
              <>
                <div className="space-y-4">
                  <h3 className="text-lg font-black text-gray-900 flex items-center">
                    <span className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-[14px] text-gray-400 mr-3 border border-gray-100">01</span>
                    基础配置模块
                  </h3>
                  <ul className="grid grid-cols-1 gap-4 ml-11">
                    <li className="flex items-start p-4 bg-gray-50/50 rounded-2xl border border-gray-100">
                      <div>
                        <p className="text-[14px] font-black text-gray-900">预约模式选择</p>
                        <p className="text-[13px] text-gray-500 font-medium mt-1">支持“与品牌一致”和“单独设置”。选择品牌一致时，门店同步集团规则；单独设置时，支持自定义配置。</p>
                      </div>
                    </li>
                    <li className="flex items-start p-4 bg-gray-50/50 rounded-2xl border border-gray-100">
                      <div>
                        <p className="text-[14px] font-black text-gray-900">动态日期组管理</p>
                        <p className="text-[13px] text-gray-500 font-medium mt-1">支持新增多个日期组。在每个日期组内，可自由勾选周一至周日。已在其他组勾选的日期会被置灰，确保日期配置互斥。</p>
                      </div>
                    </li>
                  </ul>
                </div>
                <div className="space-y-4">
                  <h3 className="text-lg font-black text-gray-900 flex items-center">
                    <span className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-[14px] text-gray-400 mr-3 border border-gray-100">02</span>
                    场次核心配置
                  </h3>
                  <ul className="grid grid-cols-1 gap-4 ml-11">
                    <li className="flex items-start p-4 bg-gray-50/50 rounded-2xl border border-gray-100">
                      <div>
                        <p className="text-[14px] font-black text-gray-900">场次信息维护</p>
                        <p className="text-[13px] text-gray-500 font-medium mt-1">在选定的日期组下新增场次。每个卡片包含：名称（可编辑）、时间段、预约限额步进器。限额支持一键增减。</p>
                      </div>
                    </li>
                    <li className="flex items-start p-4 bg-gray-50/50 rounded-2xl border border-gray-100">
                      <div>
                        <p className="text-[14px] font-black text-gray-900">商品关联管理</p>
                        <p className="text-[13px] text-gray-500 font-medium mt-1">点击“选择商品”或“已选商品”打开商品库层，选择该场次对应售卖的具体单品。</p>
                      </div>
                    </li>
                    <li className="flex items-start p-4 bg-gray-50/50 rounded-2xl border border-gray-100">
                      <div>
                        <p className="text-[14px] font-black text-gray-900">增删逻辑</p>
                        <p className="text-[13px] text-gray-500 font-medium mt-1">每个日期组底部“新增场次”按钮允许在10个限额内扩展。卡片右上角支持单条删除。整个日期组也可以一键删除。</p>
                      </div>
                    </li>
                  </ul>
                </div>
              </>
            ) : reqKey === 'detail' ? (
              <>
                <div className="space-y-4">
                  <h3 className="text-lg font-black text-gray-900 flex items-center">
                    <span className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-[14px] text-gray-400 mr-3 border border-gray-100">01</span>
                    页面布局展示
                  </h3>
                  <ul className="grid grid-cols-1 gap-4 ml-11">
                    <li className="flex items-start p-4 bg-gray-50/50 rounded-2xl border border-gray-100">
                      <div>
                        <p className="text-[14px] font-black text-gray-900">详情页结构</p>
                        <p className="text-[13px] text-gray-500 font-medium mt-1">分为商品信息和用户信息两个核心版块，展示订单的完整链路信息。</p>
                      </div>
                    </li>
                    <li className="flex items-start p-4 bg-gray-50/50 rounded-2xl border border-gray-100">
                      <div>
                        <p className="text-[14px] font-black text-gray-900">隐私保护</p>
                        <p className="text-[13px] text-gray-500 font-medium mt-1">联系人手机号默认隐藏中间四位，点击“睁眼”图标可切换为明文展示。</p>
                      </div>
                    </li>
                  </ul>
                </div>
                <div className="space-y-4">
                  <h3 className="text-lg font-black text-gray-900 flex items-center">
                    <span className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-[14px] text-gray-400 mr-3 border border-gray-100">02</span>
                    动态操作权限
                  </h3>
                  <ul className="grid grid-cols-1 gap-4 ml-11">
                    <li className="flex items-start p-4 bg-gray-50/50 rounded-2xl border border-gray-100">
                      <div>
                        <p className="text-[14px] font-black text-gray-900">基于状态的操作</p>
                        <p className="text-[13px] text-gray-500 font-medium mt-1">仅“待核销”状态展示操作按钮；终态订单仅供查询，隐藏底部操作栏并提示状态。</p>
                      </div>
                    </li>
                    <li className="flex items-start p-4 bg-gray-50/50 rounded-2xl border border-gray-100">
                      <div>
                        <p className="text-[14px] font-black text-gray-900">渠道逻辑限制</p>
                        <p className="text-[13px] text-gray-500 font-medium mt-1">针对第三方渠道（如抖音、美团）订单，自动隐藏“退款”按钮并显示温馨提醒。</p>
                      </div>
                    </li>
                  </ul>
                </div>
              </>
            ) : reqKey === 'redeem' ? (
              <>
                <div className="space-y-4">
                  <h3 className="text-lg font-black text-gray-900 flex items-center">
                    <span className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-[14px] text-gray-400 mr-3 border border-gray-100">01</span>
                    核心核销功能
                  </h3>
                  <ul className="grid grid-cols-1 gap-4 ml-11">
                    <li className="flex items-start p-4 bg-gray-50/50 rounded-2xl border border-gray-100">
                      <div className="w-5 h-5 rounded-full bg-[#5DA756] flex items-center justify-center shrink-0 mt-0.5 mr-3">
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" /></svg>
                      </div>
                      <div>
                        <p className="text-[14px] font-black text-gray-900">扫码核销</p>
                        <p className="text-[13px] text-gray-500 font-medium mt-1">点击页面上方“扫码核销”卡片，调起摄像头进行实时券码扫描。</p>
                      </div>
                    </li>
                    <li className="flex items-start p-4 bg-gray-50/50 rounded-2xl border border-gray-100">
                      <div className="w-5 h-5 rounded-full bg-[#5DA756] flex items-center justify-center shrink-0 mt-0.5 mr-3">
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" /></svg>
                      </div>
                      <div>
                        <p className="text-[14px] font-black text-gray-900">输入券码核销</p>
                        <p className="text-[13px] text-gray-500 font-medium mt-1">支持手动录入券码，点击“确认核销”后进行合规性校验并展示结果。</p>
                      </div>
                    </li>
                  </ul>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-black text-gray-900 flex items-center">
                    <span className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-[14px] text-gray-400 mr-3 border border-gray-100">02</span>
                    手动核销 - 模拟调试指南
                  </h3>
                  <p className="text-gray-500 text-[14px] ml-11 font-medium">系统内置预设券码，用于模拟不同核销场景：</p>
                  
                  <div className="ml-11 grid grid-cols-1 gap-3">
                    <div className="group p-5 bg-white rounded-2xl border border-gray-100 shadow-sm hover:border-[#5DA756]/20 transition-all">
                      <div className="flex items-center justify-between mb-2">
                        <code className="px-3 py-1 bg-green-50 text-[#5DA756] rounded-lg font-black text-[15px] border border-green-100">11</code>
                        <span className="text-[11px] font-black text-[#5DA756] bg-[#F6FBF6] px-2 py-0.5 rounded-full uppercase">Success</span>
                      </div>
                      <p className="text-[13px] text-gray-500 font-medium">输入后模拟成功核销，显示详情页并可以点击确认核销触发 Toast。</p>
                    </div>

                    <div className="group p-5 bg-white rounded-2xl border border-gray-100 shadow-sm hover:border-orange-200 transition-all">
                      <div className="flex items-center justify-between mb-2">
                        <code className="px-3 py-1 bg-orange-50 text-orange-600 rounded-lg font-black text-[15px] border border-orange-100">22</code>
                        <span className="text-[11px] font-black text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full uppercase">Mismatch</span>
                      </div>
                      <p className="text-[13px] text-gray-500 font-medium">模拟门店不匹配，显示对应异常弹窗并支持点击遮罩返回。</p>
                    </div>

                    <div className="group p-5 bg-white rounded-2xl border border-gray-100 shadow-sm hover:border-red-200 transition-all">
                      <div className="flex items-center justify-between mb-2">
                        <code className="px-3 py-1 bg-red-50 text-red-600 rounded-lg font-black text-[15px] border border-red-100">33</code>
                        <span className="text-[11px] font-black text-red-600 bg-red-50 px-2 py-0.5 rounded-full uppercase">Invalid</span>
                      </div>
                      <p className="text-[13px] text-gray-500 font-medium">模拟券码无效（过期或错误），显示统一失败提示弹窗。</p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-4">
                  <h3 className="text-lg font-black text-gray-900 flex items-center">
                    <span className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-[14px] text-gray-400 mr-3 border border-gray-100">01</span>
                    预约展示与筛选
                  </h3>
                  <ul className="grid grid-cols-1 gap-4 ml-11">
                    <li className="flex items-start p-4 bg-gray-50/50 rounded-2xl border border-gray-100">
                      <div className="w-5 h-5 rounded-full bg-[#5DA756] flex items-center justify-center shrink-0 mt-0.5 mr-3">
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" /></svg>
                      </div>
                      <div>
                        <p className="text-[14px] font-black text-gray-900">日期切换 Tab</p>
                        <p className="text-[13px] text-gray-500 font-medium mt-1">支持横滑选择“今天、明天”及后续日期的预约数据，默认定位至今天。</p>
                      </div>
                    </li>
                    <li className="flex items-start p-4 bg-gray-50/50 rounded-2xl border border-gray-100">
                      <div className="w-5 h-5 rounded-full bg-[#5DA756] flex items-center justify-center shrink-0 mt-0.5 mr-3">
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" /></svg>
                      </div>
                      <div>
                        <p className="text-[14px] font-black text-gray-900">状态筛选 (Radio 模式)</p>
                        <p className="text-[13px] text-gray-500 font-medium mt-1">点击状态栏，弹出单选菜单，支持按业务状态进行全局过滤。</p>
                      </div>
                    </li>
                    <li className="flex items-start p-4 bg-gray-50/50 rounded-2xl border border-gray-100">
                      <div className="w-5 h-5 rounded-full bg-[#5DA756] flex items-center justify-center shrink-0 mt-0.5 mr-3">
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" /></svg>
                      </div>
                      <div>
                        <p className="text-[14px] font-black text-gray-900">模糊搜索</p>
                        <p className="text-[13px] text-gray-500 font-medium mt-1">支持通过手机号或联系人姓名进行列表内模糊匹配搜索。</p>
                      </div>
                    </li>
                  </ul>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-black text-gray-900 flex items-center">
                    <span className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-[14px] text-gray-400 mr-3 border border-gray-100">02</span>
                    分组列表与交互
                  </h3>
                  <ul className="grid grid-cols-1 gap-4 ml-11">
                    <li className="flex items-start p-4 bg-gray-50/50 rounded-2xl border border-gray-100">
                      <div className="w-5 h-5 rounded-full bg-[#5DA756] flex items-center justify-center shrink-0 mt-0.5 mr-3">
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" /></svg>
                      </div>
                      <div>
                        <p className="text-[14px] font-black text-gray-900">按场次分组</p>
                        <p className="text-[13px] text-gray-500 font-medium mt-1">订单按具体场次时间段（如：上午场 10:00-11:30）进行聚合展示，条理清晰。</p>
                      </div>
                    </li>
                    <li className="flex items-start p-4 bg-gray-50/50 rounded-2xl border border-gray-100">
                      <div className="w-5 h-5 rounded-full bg-[#5DA756] flex items-center justify-center shrink-0 mt-0.5 mr-3">
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" /></svg>
                      </div>
                      <div>
                        <p className="text-[14px] font-black text-gray-900">详情页跳转</p>
                        <p className="text-[13px] text-gray-500 font-medium mt-1">点击任意预约卡片，全屏弹出详情页（z-index 层级最高），支持直接核销。核销成功后通过 Toast 提示，3秒后自动消失。</p>
                      </div>
                    </li>
                  </ul>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default App;
